import type { SelectedOptions } from '@corratech/pylot-variant-selector'
import type { UseOptionSelectorReturn } from '@corratech/pylot-variant-selector/src/hooks/useOptionSelector'
import type { Option } from '@pylot-data/hooks/product/use-product-ui'
import { OptionType } from '@pylot-data/hooks/product/use-product-ui'
import { COLOR_SWATCH_DATA } from '@corratech/pylot-variant-selector/src/hooks/useOptionSelector/utils/getOptionType'
import { useTranslation } from 'next-i18next'
import type { ReactElement } from 'react'
import React, { useEffect } from 'react'
import Swatch from '../Swatch/Swatch'
import s from '@corratech/pylot-swatches/src/SwatchGroup/SwatchGroup.module.scss'
import cn from 'classnames'
import {
    ConfigurableProductOptionsValues,
    Maybe,
    ImageSwatchData,
    ColorSwatchData,
    TextSwatchData
} from '@pylot-data/pylotschema'
import { sortAlphabetically } from '@components/alfakher/pylot-utils'

type SwatchGroupProps = Omit<UseOptionSelectorReturn, 'options'> & {
    option: Option
    selectedOptions: SelectedOptions
    isCentered?: boolean
    disabledValues?: string[]
}

type ConfigurableProductOptions = ConfigurableProductOptionsValues & {
    swatch_data?: Maybe<ImageSwatchData | ColorSwatchData | TextSwatchData>
}

export const SwatchGroup = ({
    option,
    selectedOptions,
    disabledValues = [],
    setSelectedOptions,
    isCentered
}: SwatchGroupProps): ReactElement => {
    const { t } = useTranslation('common')
    const active =
        selectedOptions?.[option?.code as keyof SelectedOptions] ?? ''

    useEffect(() => {
        if (option?.code === 'ooka_color') {
            const values = option?.values?.filter(
                (value) => !disabledValues?.includes(value.label as string)
            )
            setSelectedOptions((state) => ({
                ...state,
                ooka_color: values?.[0]?.label as string
            }))
        }
    }, [option])

    if (option?.type === OptionType.Dropdown) {
        const sortedValues = option?.values?.sort((a, b) =>
            sortAlphabetically(a.label, b.label)
        )

        return (
            <div className={cn('swatch-group-wrap', s['swatch-group'])}>
                {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                <select
                    className="form-select block appearance-none w-full bg-grey-lighter border-2 border-grey-lighter text-p-1 py-3 px-4 pr-8 rounded focus:shadow-none focus:border-accents-2 max-w-sm"
                    id="grid-state"
                    value={active}
                    onChange={(e) => {
                        e.persist()
                        setSelectedOptions((oldSelectedOptions) => {
                            return {
                                ...oldSelectedOptions,
                                [option?.code]: e.target?.value ?? ''
                            }
                        })
                    }}
                    // onBlur={() => {}}
                    // onFocus={() => {}}
                >
                    <option selected={active === ''} value="">
                        {t('Select an option...')}
                    </option>
                    {sortedValues?.map((v, i: number) => {
                        const value = v.label!
                        const OutOfstockValue: boolean = disabledValues.includes(
                            value
                        )
                        if (!OutOfstockValue) {
                            return (
                                <option
                                    key={`${v.value_index ?? value}-${i}`}
                                    value={value}
                                >
                                    {value}
                                </option>
                            )
                        }
                    })}
                </select>
            </div>
        )
    }

    const swatchContent = (
        <div
            className={cn('swatch-group-wrap', s['swatch-group'], {
                ['justify-center']: isCentered
            })}
        >
            {option?.values!.map((v: ConfigurableProductOptions, i: number) => {
                const color =
                    v.swatch_data?.__typename === COLOR_SWATCH_DATA
                        ? v.swatch_data?.value
                        : ''

                const value = v.label!
                const OutOfstockValue: boolean = disabledValues.includes(value)

                if (!OutOfstockValue) {
                    return (
                        <Swatch
                            key={`${v.value_index}-${i}`}
                            active={value === active}
                            color={color!}
                            value={value}
                            onClick={(color) => {
                                setSelectedOptions((oldSelectedOptions) => ({
                                    ...oldSelectedOptions,
                                    [option.code]: color!
                                }))
                            }}
                        />
                    )
                }
            })}
        </div>
    )

    return swatchContent
}
